import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIsAdmin } from '../api/auth';

/**
 * One catalogue item, in full.
 *
 * The list view can hold a price and a code; it cannot hold the reason to change
 * either. Three things only fit here:
 *
 *  - **What it costs and what you charge.** Adopting a camera copies the model,
 *    never the price, because a vendor's list price is not yours. Both appear
 *    side by side with the margin worked out, and the list figure carries the
 *    date it was read — a price is a fact with a date on it.
 *  - **Where it is already in use.** Editing an item that sits on four lines is
 *    a different act from editing one nobody has bought, and that belongs on
 *    screen before the edit rather than in a refusal after it.
 *  - **The specification.** Sensor, shutter, mount, pixel size: what goes on a
 *    quote and what an engineer asks for on the phone.
 */

interface Usage {
  id: number;
  name?: string;
  label?: string;
  station: string | null;
}

interface Supplier {
  model: string;
  family: string;
  scan: string;
  widthPx: number;
  heightPx: number | null;
  speed: number;
  speedUnit: string;
  interface: string;
  colour: string;
  shutter: string | null;
  spectrum: string;
  ingressProtection: string | null;
  mount: string | null;
  sensor: string | null;
  pixelSizeUm: number | null;
  listPrice: number | null;
  currency: string;
  retrievedAt: string | null;
  sourceUrl: string | null;
  withdrawn: boolean;
}

interface Profile {
  id: number;
  brand: string;
  model: string;
  role: string;
  driver: string;
  description: string | null;
  notes: string | null;
  listPrice: number | null;
  installHours: number | null;
  sku: string | null;
  vendor: string | null;
  vendorRef: string | null;
  vendorUrl: string | null;
  verified: boolean;
  verifiedBy: string | null;
  verifiedAt: string | null;
  required: string[];
  defaultSettings: Record<string, unknown>;
  usedOn: Usage[];
  usedInProposals: Usage[];
  supplier: Supplier | null;
}

const label: React.CSSProperties = { fontSize: 12, marginBottom: 4 };
const row: React.CSSProperties = { display: 'flex', gap: 18, flexWrap: 'wrap' };

export function CatalogueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const [p, setP] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/device-profiles/${id}`);
      if (!res.ok) throw new Error((await res.json()).message ?? 'Could not load');
      setP(await res.json());
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Save one field. Blur-to-save, as in the list: no form to remember to submit. */
  const save = async (patch: Record<string, unknown>) => {
    setError(null);
    try {
      const res = await fetch(`/api/device-profiles/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error((await res.json()).message ?? 'Could not save');
      setNotice('Saved');
      setTimeout(() => setNotice(null), 1500);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async () => {
    setError(null);
    const res = await fetch(`/api/device-profiles/${id}`, { method: 'DELETE' });
    if (res.status === 204) {
      navigate('/catalogue');
      return;
    }
    setError((await res.json()).message ?? 'Could not remove');
  };

  if (error && !p) return <div className="panel" style={{ color: 'var(--bad)' }}>{error}</div>;
  if (!p) return <div className="spinner" style={{ padding: 40 }}>Loading…</div>;
  if (!isAdmin) return <div className="panel muted">The catalogue requires the ADMIN role.</div>;

  const inUse = p.usedOn.length + p.usedInProposals.length;
  const list = p.supplier?.listPrice ?? null;
  const mine = p.listPrice;
  const margin = list !== null && mine !== null && mine > 0 ? ((mine - list) / mine) * 100 : null;
  const money = (v: number | null) => (v === null ? '—' : `€ ${v.toLocaleString('nl-NL')}`);
  const age = p.supplier?.retrievedAt
    ? Math.round((Date.now() - new Date(p.supplier.retrievedAt).getTime()) / 86_400_000)
    : null;

  return (
    <div>
      <Link to="/catalogue" className="muted" style={{ fontSize: 13 }}>
        ← Catalogue
      </Link>
      <h1 style={{ marginTop: 6, marginBottom: 2 }}>{p.model}</h1>
      <div className="muted" style={{ marginBottom: 16 }}>
        {p.brand} · {p.role} · driver {p.driver}
        {p.description ? ` · ${p.description}` : ''}
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 12 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 12 }}>{notice}</div>}

      {p.supplier?.withdrawn && (
        <div className="panel" style={{ marginBottom: 12, borderColor: 'var(--bad)' }}>
          The supplier no longer lists this model. It stays here because it may already be on a
          line or a quote, but check availability before selling another.
        </div>
      )}

      <div className="panel" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Money</h3>
        <div style={row}>
          <div>
            <div className="muted" style={label}>Supplier list price</div>
            <div style={{ fontSize: 20, fontVariantNumeric: 'tabular-nums' }}>{money(list)}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              {list === null
                ? 'Quoted on request — not published'
                : age === null
                  ? 'date unknown'
                  : `read ${age === 0 ? 'today' : `${age} day${age === 1 ? '' : 's'} ago`}`}
            </div>
          </div>
          <Editable
            title="Your price"
            value={mine === null ? '' : String(mine)}
            placeholder="not set"
            onSave={(v) => save({ listPrice: v === '' ? null : Number(v) })}
            hint="What you charge. Blank means this item cannot be quoted."
            wide
          />
          <div>
            <div className="muted" style={label}>Margin</div>
            <div
              style={{
                fontSize: 20,
                fontVariantNumeric: 'tabular-nums',
                color: margin === null ? undefined : margin < 0 ? 'var(--bad)' : 'var(--good)',
              }}
            >
              {margin === null ? '—' : `${margin.toFixed(0)}%`}
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              {margin === null
                ? 'needs both prices'
                : margin < 0
                  ? 'you are selling below list'
                  : `€ ${(mine! - list!).toLocaleString('nl-NL')} per unit`}
            </div>
          </div>
          <Editable
            title="Install hours"
            value={p.installHours === null ? '' : String(p.installHours)}
            placeholder="—"
            onSave={(v) => save({ installHours: v === '' ? null : Number(v) })}
            hint="Labour quoted alongside it"
          />
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Ordering</h3>
        <div style={row}>
          <Editable
            title="SKU"
            value={p.sku ?? ''}
            placeholder="none"
            onSave={(v) => save({ sku: v })}
            hint="What the invoicing sync matches on"
            wide
          />
          <Editable
            title="Supplier"
            value={p.vendor ?? ''}
            placeholder="none"
            onSave={(v) => save({ vendor: v })}
            wide
          />
          <Editable
            title="Their reference"
            value={p.vendorRef ?? ''}
            placeholder="none"
            onSave={(v) => save({ vendorRef: v })}
            hint="Order code, if it differs"
            wide
          />
        </div>
        {p.vendorUrl && (
          <div style={{ marginTop: 10 }}>
            <a href={p.vendorUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
              Supplier documentation ↗
            </a>
          </div>
        )}
      </div>

      {p.supplier && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Specification</h3>
          <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
            From the supplier's published table, not editable here — a figure you can change is a
            figure that stops matching the datasheet.
          </div>
          <div style={{ ...row, gap: 26 }}>
            <Spec name="Resolution">
              {p.supplier.widthPx.toLocaleString('nl-NL')}
              {p.supplier.scan === 'line'
                ? ' px/line'
                : p.supplier.heightPx
                  ? ` × ${p.supplier.heightPx.toLocaleString('nl-NL')}`
                  : ' px'}
            </Spec>
            <Spec name={p.supplier.scan === 'line' ? 'Line rate' : 'Frame rate'}>
              {p.supplier.speed} {p.supplier.speedUnit}
            </Spec>
            <Spec name="Interface">{p.supplier.interface}</Spec>
            <Spec name="Sensor">{p.supplier.sensor ?? 'not published'}</Spec>
            <Spec name="Pixel size">
              {p.supplier.pixelSizeUm === null ? 'not published' : `${p.supplier.pixelSizeUm} µm`}
            </Spec>
            <Spec name="Shutter" warn={p.supplier.shutter === null}>
              {p.supplier.shutter ?? 'not published — check before a moving belt'}
            </Spec>
            <Spec name="Mount">{p.supplier.mount ?? 'not published'}</Spec>
            <Spec name="Colour">{p.supplier.colour}</Spec>
            <Spec name="Spectrum" warn={p.supplier.spectrum !== 'visible'}>
              {p.supplier.spectrum}
              {p.supplier.spectrum !== 'visible' && ' — needs its own lens and lighting'}
            </Spec>
            {p.supplier.ingressProtection && (
              <Spec name="Housing">{p.supplier.ingressProtection}</Spec>
            )}
          </div>
        </div>
      )}

      <div className="panel" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Where it is used</h3>
        {inUse === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            Not on any line or proposal. Safe to change or remove.
          </div>
        ) : (
          <div style={{ fontSize: 13 }}>
            {p.usedOn.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <strong>{p.usedOn.length} line device(s):</strong>{' '}
                {p.usedOn.map((d) => `${d.name}${d.station ? ` (${d.station})` : ''}`).join(', ')}
              </div>
            )}
            {p.usedInProposals.length > 0 && (
              <div>
                <strong>{p.usedInProposals.length} proposal item(s):</strong>{' '}
                {p.usedInProposals.map((d) => d.label).join(', ')}
              </div>
            )}
            <div className="muted" style={{ marginTop: 8 }}>
              A price change here does not rewrite a proposal that has already been sent — those
              keep the figure they were quoted at.
            </div>
          </div>
        )}
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Notes and status</h3>
        <textarea
          defaultValue={p.notes ?? ''}
          placeholder="Anything the next person should know — wiring quirks, lead time, who to call."
          rows={3}
          style={{ width: '100%', maxWidth: 640 }}
          onBlur={(e) => e.target.value !== (p.notes ?? '') && save({ notes: e.target.value })}
        />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 14 }}>
          <button
            className={`btn sm${p.verified ? '' : ' primary'}`}
            onClick={async () => {
              await fetch(`/api/device-profiles/${p.id}/verify`, { method: 'POST' });
              await load();
            }}
          >
            {p.verified ? 'Verified ✓' : 'Mark verified'}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            {p.verified
              ? `by ${p.verifiedBy ?? 'someone'}${p.verifiedAt ? ` on ${p.verifiedAt.slice(0, 10)}` : ''}`
              : 'Verified means someone has run this device, not just entered it.'}
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn sm" style={{ color: 'var(--bad)' }} onClick={() => void remove()}>
            Remove from catalogue
          </button>
        </div>
        {p.required.length > 0 && (
          <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
            Needs {p.required.join(', ')} filled in per device before the HAL will drive it.
          </div>
        )}
      </div>
    </div>
  );
}

function Spec({
  name,
  children,
  warn,
}: {
  name: string;
  children: React.ReactNode;
  warn?: boolean;
}) {
  return (
    <div>
      <div className="muted" style={label}>{name}</div>
      <div style={{ fontSize: 14, color: warn ? 'var(--warn, #f59e0b)' : undefined }}>{children}</div>
    </div>
  );
}

function Editable({
  title,
  value,
  placeholder,
  onSave,
  hint,
  wide,
}: {
  title: string;
  value: string;
  placeholder?: string;
  onSave: (v: string) => void;
  hint?: string;
  wide?: boolean;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div>
      <div className="muted" style={label}>{title}</div>
      <input
        value={v}
        placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== value && onSave(v.trim())}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        style={{ width: wide ? 190 : 120 }}
      />
      {hint && (
        <div className="muted" style={{ fontSize: 11, marginTop: 3, maxWidth: 190 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
